<template>
  <div class="large-tree-wrapper">
    <a-input
      v-model:value="searchValue"
      placeholder="搜索节点"
      style="width: 98%; margin-bottom: 16px"
      :allowClear="true"
      size="small"
      @input="handleSearch"
    />

    <div
      class="large-tree-container"
      ref="treeContainer"
      @scroll="handleContainerScroll"
    >
      <a-tree
        :tree-data="displayData"
        :virtual="false"
        :load-data="onLoadData"
        :expanded-keys.sync="expandedKeys"
        :auto-expand-parent="true"
        :default-expand-all="false"
        :selected-keys.sync="selectedKeys"
        :checkable="true"
        :checked-keys.sync="checkedKeys"
        :show-line="true"
        :replace-fields="{ key: 'key', title: 'title', children: 'children' }"
        @expand="onExpand"
        @select="onSelect"
        @check="onCheck"
      >
        <template #title="{ node }">
          <div class="tree-node-content" :data-node-key="node.value">
            {{ node.title }}
            <span v-if="node.childCount > 1000" class="child-count-badge">
              {{ node.childCount > 10000 ? "10000+" : `${node.childCount}` }}
            </span>
          </div>
        </template>
      </a-tree>

      <!-- 加载中指示器 -->
      <div v-if="loadingMore" class="loading-indicator">
        <a-spin size="small" />
        <span style="margin-left: 8px">加载中...</span>
      </div>
    </div>
  </div>
</template>

<script>
import {
  getParentNodes,
  getChildNodesByParentId,
  searchNodes,
} from "@/api/tree";
export default {
  props: {
    preCheckedKeys: {
      type: Array,
      default: function () {
        return [];
      },
    },
  },
  data() {
    return {
      // 搜索相关
      searchValue: "",
      searchResult: [],

      // 展开和选中状态
      expandedKeys: [],
      selectedKeys: [],
      checkedKeys: [],

      // 原始数据缓存
      treeDataCache: new Map(),

      // 子节点分页数据缓存
      childPageCache: new Map(),

      // 显示数据
      displayData: [],

      // 加载状态管理
      loadingKeys: new Set(),
      loadingMore: false, // 新增：底部加载状态

      // 大数据节点标记
      largeDataNodes: new Set(),

      // 防抖定时器
      debounceTimer: null,
      scrollDebounceTimer: null,

      // 搜索模式
      isSearchMode: false,

      // 当前正在加载更多的节点ID
      currentLoadingNodeId: null,
    };
  },
  // 添加 watch 监听 preCheckedKeys 变化
  watch: {
    preCheckedKeys: {
      handler(newVal) {
        if (newVal && Array.isArray(newVal) && newVal.length > 0) {
          this.setCheckedKeys(newVal);
        }
      },
      immediate: true,
      deep: true,
    },
  },
  mounted() {
    // 初始化加载根节点
    this.loadRootNodes();
  },

  methods: {
    // 加载根节点
    async loadRootNodes() {
      try {
        console.log("开始加载根节点");
        const response = await this.fetchNodes(null, 1, 100);
        console.log("根节点数据:", response.data);

        // 转换数据格式并保存原始信息
        this.displayData = response.data.map((item) => {
          // 根据childCount判断是否有子节点
          const hasChildren = item.childCount > 0;
          console.log(
            `节点${item.id}有子节点: ${hasChildren}, 数量: ${item.childCount}`
          );
          return {
            key: item.id,
            title: item.title,
            value: item.id,
            text: item.title,
            childCount: item.childCount,
            isLeaf: !hasChildren, // 关键点：根据childCount设置是否为叶子节点
            children: [],
            ...item,
          };
        });

        // 重新填充大数据节点标记
        this.largeDataNodes.clear();
        response.data.forEach((node) => {
          if (node.childCount > 1000) {
            this.largeDataNodes.add(node.id);
          }
        });

        // 确保在数据加载完成后设置预选节点
        this.$nextTick(() => {
          console.log("根节点数据加载完成，强制更新UI");
          this.setCheckedKeys(this.preCheckedKeys);
          this.$forceUpdate();
        });
      } catch (error) {
        console.error("加载根节点失败:", error);
        this.$message.error("加载数据失败，请重试");
      }
    },
    // 新增方法：设置选中节点
    setCheckedKeys(keysToCheck) {
      if (
        !keysToCheck ||
        !Array.isArray(keysToCheck) ||
        keysToCheck.length === 0
      ) {
        return;
      }

      console.log("设置预选节点:", keysToCheck);

      // 过滤有效的节点ID
      const validKeys = keysToCheck.filter(
        (key) => key !== null && key !== undefined && key !== ""
      );

      // 更新 checkedKeys
      this.checkedKeys = validKeys;

      // 展开包含预选节点的父节点
      this.expandNodesForCheckedItems(validKeys);
    },

    // 新增方法：展开包含预选节点的父节点
    async expandNodesForCheckedItems(keys) {
      // 找出需要展开的父节点
      const nodesToExpand = new Set();

      // 为每个预选节点查找并展开其父节点
      for (const key of keys) {
        // 简单实现：这里可以根据实际需求扩展为递归查找父节点
        // 这里假设API可以获取节点的父级信息
        try {
          // 如果需要更复杂的父节点展开逻辑，可以在这里添加
          // 例如：获取节点路径并展开所有祖先节点
        } catch (error) {
          console.error(`获取节点${key}的父节点信息失败:`, error);
        }
      }

      // 合并需要展开的节点键
      if (nodesToExpand.size > 0) {
        this.expandedKeys = [
          ...this.expandedKeys,
          ...Array.from(nodesToExpand),
        ];
      }
    },
    // 修复onLoadData方法，添加对预选节点的支持
    async onLoadData(treeNode) {
      const nodeId = treeNode.value;
      console.log(`尝试加载节点: ${nodeId}`);

      // 确保treeNode和dataRef存在
      if (!treeNode || !treeNode.dataRef) {
        console.error("无效的treeNode对象");
        return Promise.reject(new Error("无效的节点对象"));
      }

      // 获取节点的childCount信息
      const nodeInfo = this.findNodeInTree(this.displayData, nodeId);
      const childCount = nodeInfo?.childCount || 0;
      console.log(`节点信息:`, nodeInfo, `子节点数量: ${childCount}`);

      // 如果已经缓存或正在加载，直接返回
      if (this.treeDataCache.has(nodeId) || this.loadingKeys.has(nodeId)) {
        if (this.treeDataCache.has(nodeId)) {
          treeNode.dataRef.children = this.treeDataCache.get(nodeId);
          treeNode.dataRef.isLeaf = false; // 确保设置在dataRef上

          // 在数据加载后检查是否需要勾选节点
          this.$nextTick(() => {
            this.setCheckedKeys(this.preCheckedKeys);
            this.$forceUpdate();
          });
        }
        return Promise.resolve();
      }

      this.loadingKeys.add(nodeId);

      try {
        // 根据节点子节点数量决定初始页面大小
        const initialPageSize = this.largeDataNodes.has(nodeId) ? 30 : 50;
        console.log(`加载节点 ${nodeId}，第一页，每页${initialPageSize}条`);

        const response = await this.fetchNodes(nodeId, 1, initialPageSize);
        console.log(`获取到数据:`, response.data);

        // 使用节点的childCount作为总数量
        const totalCount = childCount;
        const childNodes = this.convertToTreeFormat(response.data);

        // 关键修复：确保正确设置children和isLeaf
        treeNode.dataRef.children = childNodes;
        treeNode.dataRef.isLeaf = false;

        // 缓存数据
        this.treeDataCache.set(nodeId, childNodes);

        // 缓存分页信息
        const pageKey = `${nodeId}_1`;
        this.childPageCache.set(pageKey, {
          data: childNodes,
          page: 1,
          total: totalCount,
          hasMore: totalCount > initialPageSize,
        });

        this.$nextTick(() => {
          console.log("子节点数据加载完成，强制更新UI");
          // 在子节点加载完成后检查是否需要勾选
          this.setCheckedKeys(this.preCheckedKeys);
          this.$forceUpdate();
        });

        // 初始化滚动监听
        setTimeout(() => {
          const container = this.$refs.treeContainer;
          if (container) {
            this.checkIfLoadMore(container);
          }
        }, 300);

        return Promise.resolve();
      } catch (error) {
        console.error(`加载子节点失败:`, error);
        this.$message.error(`加载子节点失败: ${error.message}`);
        return Promise.reject(error);
      } finally {
        this.loadingKeys.delete(nodeId);
      }
    },

    // 修复onExpand方法，确保节点展开时正确处理缓存
    onExpand(expandedKeys) {
      this.expandedKeys = expandedKeys;

      // 延迟检查是否需要加载更多，给节点加载完成的时间
      setTimeout(() => {
        // 检查是否有新展开的节点需要初始化分页信息
        for (const nodeId of expandedKeys) {
          // 如果该节点没有分页缓存，但有子节点，尝试初始化
          const nodeInfo = this.findNodeInTree(this.displayData, nodeId);
          const hasPageCache = Array.from(this.childPageCache.keys()).some(
            (key) => key && key.startsWith(`${nodeId}_`)
          );

          if (nodeInfo && nodeInfo.childCount > 0 && !hasPageCache) {
            console.log(`为新展开的节点 ${nodeId} 初始化分页信息`);
            // 触发加载更多，初始化第一页
            this.loadMoreChildren(nodeId, 1);
            break; // 一次只处理一个节点
          }
        }
      }, 300);
    },

    // 关键修改：监听容器滚动事件
    handleContainerScroll(event) {
      // 防抖处理
      if (this.scrollDebounceTimer) {
        clearTimeout(this.scrollDebounceTimer);
      }

      this.scrollDebounceTimer = setTimeout(() => {
        // 添加调试日志
        this.checkIfLoadMore(event.target);
      }, 50); // 减少防抖时间提高响应速度
    },

    // 优化滚动检测逻辑
    checkIfLoadMore(container) {
      // 如果正在加载中，不重复触发
      if (this.loadingMore) {
        return;
      }

      const { scrollTop, scrollHeight, clientHeight } = container;
      const distanceToBottom = scrollHeight - scrollTop - clientHeight;
      // 当滚动到距离底部20px时触发加载更多，更宽松的条件
      if (distanceToBottom <= 20) {
        this.loadMoreForVisibleExpandedNodes();
      }
    },

    // 查找可视区域内的展开节点并尝试加载更多
    findVisibleExpandedNodes() {
      // 遍历所有展开的节点
      for (const nodeId of this.expandedKeys) {
        // 检查是否有更多数据可加载
        const currentPageInfo = this.getLatestPageInfo(nodeId);
        if (currentPageInfo && currentPageInfo.hasMore && !this.loadingMore) {
          // 开始加载更多
          this.loadMoreChildren(nodeId, currentPageInfo.page + 1);
          break; // 一次只加载一个节点的数据
        }
      }
    },
    // 简化loadMoreForVisibleExpandedNodes方法
    loadMoreForVisibleExpandedNodes() {
      // 过滤空节点ID
      const validExpandedKeys = this.expandedKeys.filter(
        (key) => key && typeof key === "string"
      );
      // 遍历所有有效展开的节点
      for (const nodeId of validExpandedKeys) {
        // 获取最新的分页信息
        const latestPageInfo = this.getLatestPageInfo(nodeId);

        if (latestPageInfo) {
          // 如果有更多数据且当前没有加载中
          if (latestPageInfo.hasMore && !this.loadingMore) {
            // 关键修复：如果是默认创建的分页信息(page=0)，则加载第一页
            const nextPage =
              latestPageInfo.page === 0 ? 1 : latestPageInfo.page + 1;
            this.loadMoreChildren(nodeId, nextPage);
            return; // 一次只加载一个节点
          }
        } else {
          console.log(`未找到节点 ${nodeId} 的分页信息，尝试直接加载第一页`);
          // 尝试直接加载第一页
          this.loadMoreChildren(nodeId, 1);
          return;
        }
      }
    },
    // 优化getLatestPageInfo方法，确保即使没有找到分页信息也能返回默认值
    getLatestPageInfo(nodeId) {
      let maxPage = 0;
      let latestPageInfo = null;
      let totalItems = 0;
      let loadedItems = 0;

      // 首先尝试从节点信息中获取总子节点数量
      const nodeInfo = this.findNodeInTree(this.displayData, nodeId);
      if (nodeInfo && nodeInfo.childCount !== undefined) {
        totalItems = nodeInfo.childCount;
      }

      // 遍历所有该节点的分页信息
      this.childPageCache.forEach((info, key) => {
        if (key && key.startsWith(`${nodeId}_`)) {
          const page = parseInt(key.split("_")[1]);
          if (page > maxPage) {
            maxPage = page;
            latestPageInfo = info;
          }
          // 累加已加载的项目数量
          if (info.data && info.data.length) {
            loadedItems += info.data.length;
          }
          // 如果没有从节点信息获取到总数量，则从缓存中获取
          if (info.total && totalItems === 0) {
            totalItems = info.total;
          }
        }
      });

      // 如果找到了分页信息，重新计算hasMore状态
      if (latestPageInfo && totalItems > 0) {
        // 重新计算是否还有更多数据
        latestPageInfo.hasMore = loadedItems < totalItems;
      }

      // 关键修复：如果没有找到分页信息，但有节点信息，创建默认的第一页信息
      if (!latestPageInfo && nodeInfo && nodeInfo.childCount > 0) {
        const initialPageSize = this.largeDataNodes.has(nodeId) ? 30 : 50;
        latestPageInfo = {
          data: [],
          page: 0, // 标记为0，表示还未加载过
          total: nodeInfo.childCount,
          hasMore: true,
        };
      }

      return latestPageInfo;
    },

    // 修复loadMoreChildren方法
    async loadMoreChildren(nodeId, page) {
      const pageKey = `${nodeId}_${page}`;

      if (
        this.childPageCache.has(pageKey) ||
        this.loadingKeys.has(pageKey) ||
        this.loadingMore
      ) {
        return;
      }

      this.loadingMore = true;
      this.currentLoadingNodeId = nodeId;
      this.loadingKeys.add(pageKey);

      try {
        // 获取节点的总子节点数量
        const nodeInfo = this.findNodeInTree(this.displayData, nodeId);
        const totalCount = nodeInfo?.childCount || 0;

        const pageSize = this.largeDataNodes.has(nodeId) ? 20 : 30;
        const response = await this.fetchNodes(nodeId, page, pageSize);

        if (!response.data || !Array.isArray(response.data)) {
          return;
        }

        const newNodes = this.convertToTreeFormat(response.data);

        // 找到对应的节点并追加子节点
        // 找到对应的节点并追加子节点
        const parentNode = this.findNodeInTree(this.displayData, nodeId);
        if (parentNode && parentNode.children) {
          // 记录添加前的子节点数量
          const beforeCount = parentNode.children.length;

          // 修复：避免添加重复的节点，检查key是否已存在
          const existingKeys = new Set(
            parentNode.children.map((node) => node.key)
          );
          const uniqueNewNodes = newNodes.filter(
            (node) => !existingKeys.has(node.key)
          );

          // 仅追加不重复的新节点
          parentNode.children.push(...uniqueNewNodes);
          // 计算已加载的总数
          const loadedCount = parentNode.children.length;
          const hasMore = loadedCount < totalCount;

          // 更新缓存
          this.childPageCache.set(pageKey, {
            data: newNodes,
            page,
            total: totalCount, // 使用节点的childCount作为总数量
            hasMore,
          });

          // 在nextTick中强制更新组件
          this.$nextTick(() => {
            console.log("强制更新组件");
            this.$forceUpdate();
          });
        } else {
          console.error(`未找到父节点或子节点列表: ${nodeId}`);
        }
      } catch (error) {
        console.error(`加载节点 ${nodeId} 第 ${page} 页数据失败:`, error);
        this.$message.error("加载更多数据失败");
      } finally {
        console.log(`完成加载节点 ${nodeId} 第 ${page} 页数据`);
        this.loadingMore = false;
        this.currentLoadingNodeId = null;
        this.loadingKeys.delete(pageKey);
      }
    },

    // 处理节点展开
    onExpand(expandedKeys, info) {
      this.expandedKeys = expandedKeys;
      console.log(`Expanded keys updated: ${expandedKeys.length}`);

      // 当展开节点时，延迟检查是否需要初始化加载
      setTimeout(() => {
        const container = this.$refs.treeContainer;
        if (container) {
          this.checkIfLoadMore(container);
        }
      }, 300);
    },

    // 在树中查找指定节点
    findNodeInTree(treeData, nodeId) {
      for (const node of treeData) {
        if (node.key === nodeId) {
          return node;
        }
        if (node.children && node.children.length > 0) {
          const found = this.findNodeInTree(node.children, nodeId);
          if (found) {
            return found;
          }
        }
      }
      return null;
    },

    // 处理节点选择
    onSelect(selectedKeys, info) {
      this.selectedKeys = selectedKeys;
      this.$emit("selected", selectedKeys);
    },

    // 处理节点勾选
    onCheck(checkedKeys, info) {
      this.checkedKeys = checkedKeys;
      this.$emit("checked", checkedKeys, info);
    },

    // 处理搜索（带防抖）
    // 修改handleSearch方法，正确处理event对象
    handleSearch(event) {
      // 获取实际的输入值
      const value = event.target ? event.target.value : event;

      if (this.debounceTimer) {
        clearTimeout(this.debounceTimer);
      }

      this.debounceTimer = setTimeout(() => {
        this.performSearch(value);
      }, 300);
    },

    // 修改performSearch方法，确保完全重置并正确更新UI
    async performSearch(keyword) {
      // 安全地处理keyword，转换为字符串并trim
      const searchKeyword = String(keyword || "").trim();

      if (!searchKeyword) {
        this.isSearchMode = false;

        // 1. 清除所有相关缓存和状态
        this.childPageCache.clear();
        this.treeDataCache.clear();
        this.largeDataNodes.clear();
        this.loadingKeys.clear(); // 额外清除加载中的节点标记
        this.loadingMore = false; // 重置加载更多状态

        // 2. 重置展开状态
        this.expandedKeys = [];

        // 3. 重新加载根节点
        await this.loadRootNodes();

        // 4. 强制更新组件，确保UI完全刷新
        this.$nextTick(() => {
          console.log("强制更新组件");
          this.$forceUpdate();
        });

        return;
      }

      this.isSearchMode = true;
      try {
        const response = await this.searchNodes(searchKeyword, 1, 100);
        this.displayData = this.convertToTreeFormat(response.data, true);
        this.expandedKeys = response.data.map((item) => item.id);
      } catch (error) {
        console.error("搜索节点失败:", error);
        this.$message.error("搜索失败，请重试");
      }
    },

    // 修改convertToTreeFormat方法，确保正确设置isLeaf
    convertToTreeFormat(data, isSearchResult = false) {
      return data.map((item) => {
        const hasChildren =
          item.hasChildren || (item.childCount && item.childCount > 0);
        return {
          key: item.id,
          title: item.name || item.title,
          value: item.id,
          childCount: item.childCount || 0,
          isLeaf: isSearchResult ? !!item.isLeaf : !hasChildren,
          children: [],
          ...item,
        };
      });
    },

    // 获取节点数据
    async fetchNodes(parentId, page, pageSize) {
      try {
        if (parentId === null) {
          return await getParentNodes(page, pageSize);
        } else {
          return await getChildNodesByParentId(parentId, page, pageSize);
        }
      } catch (error) {
        console.error("fetchNodes error:", error);
        throw error;
      }
    },

    // 搜索节点方法
    async searchNodes(keyword, page, pageSize) {
      try {
        const res = await searchNodes(keyword, page, pageSize);
        if (res.code === 0) {
          return res;
        }
        throw new Error(res.msg || "搜索失败");
      } catch (error) {
        console.error("searchNodes error:", error);
        return {
          code: 0,
          data: [],
          total: 0,
        };
      }
    },

    clearAllChecked() {
      // 清空选中的节点数组
      this.checkedKeys = [];

      // 构造空的info对象，保持与onCheck方法相同的参数格式
      const emptyInfo = {
        checked: [],
        checkedNodes: [],
        halfChecked: [],
        halfCheckedNodes: [],
        node: null,
        event: null,
      };

      // 发出checked事件，通知父组件选择状态已变更
      this.$emit("checked", this.checkedKeys, emptyInfo);

      // 强制组件更新以确保UI正确显示
      this.$nextTick(() => {
        this.$forceUpdate();
      });
    },
  },

  beforeUnmount() {
    // 清理定时器
    if (this.debounceTimer) {
      clearTimeout(this.debounceTimer);
    }
    if (this.scrollDebounceTimer) {
      clearTimeout(this.scrollDebounceTimer);
    }

    // 清理缓存
    this.treeDataCache.clear();
    this.childPageCache.clear();
    this.loadingKeys.clear();
    this.largeDataNodes.clear();
  },
};
</script>

<style scoped>
.large-tree-wrapper {
  width: 100%;
  position: relative;
}

.large-tree-container {
  height: 400px;
  overflow: auto;
  width: 100%;
}

:deep(.ant-tree) {
  min-height: 100%;
  width: 100%;
}

.tree-node-content {
  display: flex;
  align-items: center;
  min-width: 0;
}

.child-count-badge {
  margin-left: 8px;
  color: #1890ff;
  font-size: 12px;
}

.loading-indicator {
  display: flex;
  justify-content: center;
  align-items: center;
  padding: 16px;
  background: #fafafa;
}
.large-tree-container::-webkit-scrollbar {
  width: 10px;
  height: 10px;
}

.large-tree-container::-webkit-scrollbar-track {
  background: #f1f1f1;
  border-radius: 4px;
}

.large-tree-container::-webkit-scrollbar-thumb {
  background: #c1c1c1;
  border-radius: 4px;
}

.large-tree-container::-webkit-scrollbar-thumb:hover {
  background: #a8a8a8;
}
</style>
